import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Loader, AlertTriangle, CheckCircle } from 'lucide-react';

const Forgot = () => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        // This is a placeholder for your actual API call
        try {
            // Replace with your API call, e.g., await axios.post('/api/auth/forgot-password', { email });
            await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate network request
            
            setMessage('If an account with that email exists, a password reset link has been sent.');
            setEmail('');
        } catch (err) {
            setError('Failed to send reset link. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <Helmet>
            <title>Forgot Password | RatingApp</title>
            <meta name="description" content="Reset your RatingApp account password to regain access and continue earning USDT." />
        </Helmet>
        <div className="flex items-center justify-center min-h-screen  from-gray-900     via-blue-900/30 to-gray-900 text-white p-4">
            <motion.div 
                className="w-full max-w-md"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="bg-gray-800/30 backdrop-blur-xl border border-white/10 shadow-2xl shadow-cyan-500/10 rounded-3xl p-8">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                            Reset Password
                        </h1>
                        <p className="text-slate-400 mt-2">Enter your email to receive a reset link.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="relative">
                             <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                            <input
                                type="email"
                                name="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email Address"
                                required
                                className="w-full bg-gray-900/50 border border-transparent hover:border-white/20 focus:border-cyan-500 rounded-xl py-3 pl-12 pr-4 text-white placeholder-slate-400 transition-all duration-300 focus:ring-2 focus:ring-cyan-500/50"
                            />
                        </div>

                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 p-3 rounded-lg"
                                >
                                    <AlertTriangle size={18} />
                                    <span>{error}</span>
                                </motion.div>
                            )}
                            {message && (
                                 <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 p-3 rounded-lg"
                                >
                                    <CheckCircle size={18} />
                                    <span>{message}</span>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <motion.button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg py-3 rounded-xl shadow-lg shadow-cyan-500/20 border-t border-cyan-400/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {loading ? <><Loader className="animate-spin" /> Sending Link...</> : 'Send Reset Link'}
                        </motion.button>

                        <div className="text-sm text-center text-slate-400">
                             <Link to="/login" className="hover:text-cyan-400 transition-colors">Back to Sign In</Link>
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
        </>
    );
};

export default Forgot;
